import type { ComponentType } from 'react'
import {
  LuClock,
  LuCircleHelp,
  LuShieldAlert,
  LuShieldCheck,
} from 'react-icons/lu'
import {
  VERIFICACION_PUBLICA_AUTENTICO,
  VERIFICACION_PUBLICA_NO_COINCIDE,
  VERIFICACION_PUBLICA_NO_COMPROBABLE,
  VERIFICACION_PUBLICA_NO_DISPONIBLE,
  type VerificacionPublicaResponse,
} from '../../api/models/response/indiceDigital'

/**
 * LA PUERTA PÚBLICA: resolver un código impreso y decidir cómo se pinta el veredicto.
 *
 * <p>SIN SESIÓN, SIN TENANT Y SIN EL CLIENTE DE API. Se usa `fetch` pelado y no
 * `apiClient` a propósito: aquél adjunta el token, refresca sesión y levanta toasters
 * de error. Quien abre esta página no tiene cuenta, y una petición que arrastre
 * cabeceras de sesión o dispare el flujo de refresco es un error de diseño, no un
 * detalle.</p>
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://localhost:5051/api'

// ── Resolución del código ────────────────────────────────────────────────────

/**
 * Certificado de aprobación de un minero, identificado por su CUID. Es el OTRO
 * código que puede llegar a esta misma URL.
 */
export interface CertificadoPublicoResponse {
  cuidCode: string
  ruc: string
  legalName: string
  commercialName: string | null
  authorizedAt: string
  registrationStatus: string
  minerSource: string | null
  reinfoStatus: string | null
}

/**
 * Qué resultó de teclear un código.
 *
 * `noEncontrado` y `sinConexion` SON ESTADOS DISTINTOS y no pueden fundirse: el
 * primero dice "ese código no consta", el segundo "no hemos podido preguntarlo". Un
 * fallo de red mostrado como "no consta" es la misma clase de error que pintar una
 * caída de KMS como falsificación.
 */
export type ResolucionCodigo =
  | { kind: 'indice'; data: VerificacionPublicaResponse }
  | { kind: 'certificado'; data: CertificadoPublicoResponse }
  | { kind: 'noEncontrado' }
  | { kind: 'sinConexion' }

/**
 * UNA SOLA PUERTA PARA LOS DOS CÓDIGOS, porque el backend ya la construyó así:
 * `PublicWebUrlResolver.VerificationUrl` imprime la MISMA ruta `/verificar/{código}`
 * en el QR de la carátula (CUID) y en el del Índice Digital (CVD). Quien tiene un
 * papel delante no sabe —ni tiene por qué saber— cuál de los dos está leyendo.
 *
 * <p>SE PREGUNTAN LOS DOS EXTREMOS EN PARALELO Y NO SE MIRA EL FORMATO DEL CÓDIGO.
 * Adivinar aquí de qué tipo es el código reintroduciría en el cliente justo el
 * oráculo de formato que el backend evita devolviendo un 404 idéntico para el código
 * inexistente y para el mal formado. Un código que no resuelve cuesta una consulta
 * por índice único a cada lado y no toca ni bucket ni KMS.</p>
 */
export async function resolverCodigoPublico(code: string): Promise<ResolucionCodigo> {
  const [indice, certificado] = await Promise.allSettled([
    fetchIndice(code),
    fetchCertificado(code),
  ])

  if (indice.status === 'fulfilled' && indice.value) {
    return { kind: 'indice', data: indice.value }
  }
  if (certificado.status === 'fulfilled' && certificado.value) {
    return { kind: 'certificado', data: certificado.value }
  }

  // Si una de las dos consultas ni siquiera llegó a responder, NO se puede afirmar
  // que el código no exista: no se ha comprobado.
  if (indice.status === 'rejected' || certificado.status === 'rejected') {
    return { kind: 'sinConexion' }
  }

  return { kind: 'noEncontrado' }
}

/**
 * El CVD se manda TAL CUAL se tecleó. `CvdGenerator.Normalize` del backend ya pliega
 * las confusiones reales de leer de un papel (I y L por 1, O por 0) y se come guiones
 * y espacios; comprobar el formato aquí sólo serviría para rechazar en el navegador
 * códigos que el servidor habría entendido.
 */
async function fetchIndice(code: string): Promise<VerificacionPublicaResponse | null> {
  const response = await fetch(`${API_BASE}/verificar/${encodeURIComponent(code)}`)
  if (response.status === 404) return null
  if (!response.ok) throw new Error(`verificar: ${response.status}`)
  return (await response.json()) as VerificacionPublicaResponse
}

async function fetchCertificado(code: string): Promise<CertificadoPublicoResponse | null> {
  const response = await fetch(`${API_BASE}/public/verify/${encodeURIComponent(code)}`)
  if (response.status === 404) return null
  if (!response.ok) throw new Error(`public/verify: ${response.status}`)
  return (await response.json()) as CertificadoPublicoResponse
}

// ── Cómo se pinta cada uno de los cuatro estados ─────────────────────────────

export interface VerificacionPublicaVisual {
  /** Color del icono y del título. */
  color: string
  /** Fondo de la franja de veredicto. */
  bg: string
  /** Borde inferior de la franja. */
  borderColor: string
  /** Icono. VA SIEMPRE ACOMPAÑADO DEL TÍTULO: el estado no se dice sólo con el color. */
  icon: ComponentType<{ size?: number }>
  /** Titular corto del veredicto, en texto. */
  titulo: string
  /** El veredicto afirma que algo no cuadra. SÓLO `NoCoincide`. */
  esAcusacion: boolean
  /** Merece la pena reintentar: la incidencia es pasajera. */
  reintentable: boolean
  /** Se puede enunciar qué acredita el código: sólo cuando la firma sostiene. */
  acredita: boolean
}

/**
 * CUATRO ENTRADAS EXPLÍCITAS, UNA POR ESTADO. No hay ningún `estado === Autentico ?
 * verde : rojo` ni nada que se le parezca, y es la propiedad que sostiene esta
 * pantalla entera: de los cuatro desenlaces SÓLO UNO es una acusación.
 *
 * - `Autentico` — la firma sostiene el documento.
 * - `NoCoincide` — el documento no es el que se firmó. La única respuesta negativa.
 * - `NoComprobable` — nadie puede pronunciarse y volver mañana no lo cambia. El
 *   archivo sellado nunca llegó a guardarse, o su firma no es evaluable. Tono neutro.
 * - `NoDisponible` — el servicio de verificación no responde AHORA MISMO. Tono
 *   neutro y "vuelva a intentarlo": diez minutos de caída de Cloud KMS no pueden
 *   convertirse en una acusación de falsificación publicada contra una empresa.
 */
const NO_DISPONIBLE: VerificacionPublicaVisual = {
  color: '#414844',
  bg: '#f1f4f2',
  borderColor: '#e0e3e1',
  icon: LuClock,
  titulo: 'La verificación no está disponible en este momento',
  esAcusacion: false,
  reintentable: true,
  acredita: false,
}

const VISUALES: Partial<Record<number, VerificacionPublicaVisual>> = {
  [VERIFICACION_PUBLICA_AUTENTICO]: {
    color: '#1d6664',
    bg: '#eef7f6',
    borderColor: '#bfe3df',
    icon: LuShieldCheck,
    titulo: 'Documento auténtico',
    esAcusacion: false,
    reintentable: false,
    acredita: true,
  },
  [VERIFICACION_PUBLICA_NO_COINCIDE]: {
    color: '#b23a26',
    bg: '#fff5f4',
    borderColor: '#ffdad6',
    icon: LuShieldAlert,
    titulo: 'El documento no coincide con su firma',
    esAcusacion: true,
    reintentable: false,
    acredita: false,
  },
  [VERIFICACION_PUBLICA_NO_COMPROBABLE]: {
    color: '#414844',
    bg: '#f1f4f2',
    borderColor: '#e0e3e1',
    icon: LuCircleHelp,
    titulo: 'La firma de este índice no se puede comprobar',
    esAcusacion: false,
    reintentable: false,
    acredita: false,
  },
  [VERIFICACION_PUBLICA_NO_DISPONIBLE]: NO_DISPONIBLE,
}

/**
 * EL RESPALDO CAE EN EL DESENLACE NEUTRO, igual que el `switch` del backend
 * (`VerificacionPublicaMapper.Mensaje`): si mañana se añade un estado y alguien
 * olvida su entrada aquí, lo que sale es "vuelva más tarde", nunca una acusación.
 */
export function describeVerificacionPublica(estado: number): VerificacionPublicaVisual {
  return VISUALES[estado] ?? NO_DISPONIBLE
}

/** "folio 7" para uno suelto, "folios 7 al 24" para un rango. */
export function describeFolios(desde: number, hasta: number): string {
  return hasta <= desde ? `Folio ${desde}` : `Folios ${desde} al ${hasta}`
}
